import { createSlice } from "@reduxjs/toolkit";
import {
  createOffer,
  getAllOffers,
  getMyOffers,
  fetchOfferDetails,
} from "../thunks/offerThunks";

const initialState = {
  offers: [],
  myOffers: [],
  offerDetails: null,
  loading: false,
  error: null,
  createOfferLoading: false,
  createOfferError: null,
  fetchOffersLoading: false,
  fetchOffersError: null,
  fetchMyOffersLoading: false,
  fetchMyOffersError: null,
};

const offerSlice = createSlice({
  name: "offer",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.createOfferError = null;
      state.fetchOffersError = null;
    },
    clearOfferDetails: (state) => {
      state.offerDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Create Offer
    builder
      .addCase(createOffer.pending, (state) => {
        state.createOfferLoading = true;
        state.createOfferError = null;
      })
      .addCase(createOffer.fulfilled, (state, action) => {
        state.createOfferLoading = false;
        state.offers.unshift(action.payload); // Add new offer to the beginning
      })
      .addCase(createOffer.rejected, (state, action) => {
        state.createOfferLoading = false;
        state.createOfferError = action.error.message;
      });

    // Get All Offers
    builder
      .addCase(getAllOffers.pending, (state) => {
        state.fetchOffersLoading = true;
        state.fetchOffersError = null;
      })
      .addCase(getAllOffers.fulfilled, (state, action) => {
        state.fetchOffersLoading = false;
        // Handle different response formats safely
        const responseData = action.payload;

        if (Array.isArray(responseData)) {
          // Direct array response
          state.offers = responseData;
        } else if (responseData && Array.isArray(responseData.data)) {
          // Response format: { data: [...] }
          state.offers = responseData.data;
        } else if (
          responseData &&
          responseData.data &&
          Array.isArray(responseData.data.offers)
        ) {
          // Response format: { data: { offers: [...] } }
          state.offers = responseData.data.offers;
        } else if (responseData && Array.isArray(responseData.offers)) {
          // Response format: { offers: [...] }
          state.offers = responseData.offers;
        } else {
          console.warn("Unexpected API response format:", responseData);
          state.offers = [];
        }
      })
      .addCase(getAllOffers.rejected, (state, action) => {
        state.fetchOffersLoading = false;
        state.fetchOffersError = action.payload || action.error.message;
      });

    // Get My Offers
    builder
      .addCase(getMyOffers.pending, (state) => {
        state.fetchMyOffersLoading = true;
        state.fetchMyOffersError = null;
      })
      .addCase(getMyOffers.fulfilled, (state, action) => {
        state.fetchMyOffersLoading = false;
        // Handle different response formats safely
        const responseData = action.payload;

        if (Array.isArray(responseData)) {
          // Direct array response
          state.myOffers = responseData;
        } else if (responseData && Array.isArray(responseData.data)) {
          // Response format: { data: [...] }
          state.myOffers = responseData.data;
        } else if (
          responseData &&
          responseData.data &&
          Array.isArray(responseData.data.offers)
        ) {
          // Response format: { data: { offers: [...] } }
          state.myOffers = responseData.data.offers;
        } else if (responseData && Array.isArray(responseData.offers)) {
          // Response format: { offers: [...] }
          state.myOffers = responseData.offers;
        } else {
          console.warn(
            "Unexpected getMyOffers API response format:",
            responseData
          );
          state.myOffers = [];
        }
      })
      .addCase(getMyOffers.rejected, (state, action) => {
        state.fetchMyOffersLoading = false;
        state.fetchMyOffersError = action.payload || action.error.message;
      });

    // Fetch Offer Details
    builder
      .addCase(fetchOfferDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOfferDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.offerDetails = action.payload;
      })
      .addCase(fetchOfferDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError, clearOfferDetails } = offerSlice.actions;
export default offerSlice.reducer;
